import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { AuthError, fetchAuthSession } from 'aws-amplify/auth';

type ExpressValidatorError = { param: string; msg: string };
type ZodError = { code: string; message: string; path: string[] };

export class ServerValidationError extends Error {
  errors: ExpressValidatorError[];

  // TODO: API should return uniform errors...
  constructor(errors: ExpressValidatorError[] | ZodError[]) {
    super(errors.map((e) => ('msg' in e ? e.msg : e.message)).join('\n'));
    this.errors =
      'msg' in errors[0]
        ? (errors as ExpressValidatorError[])
        : (errors as ZodError[]).map((e) => ({ param: e.path.join(), msg: e.message }));
  }
}

export class ServerError extends Error {
  constructor(message?: string) {
    super(message || 'Unexpected server error');
  }
}
export class ConflictError extends Error {
  constructor(message?: string) {
    super(message || 'Conflict error');
  }
}

export class ForbiddenError extends Error {
  constructor(message?: string) {
    super(message || 'Not Permitted');
  }
}

export const createAxiosInstance = (options: AxiosRequestConfig) => {
  const axiosInstance = axios.create(options);
  axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const session = await fetchAuthSession();
    if (session?.tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${session.tokens.accessToken}`;
    }
    return config;
  });
  axiosInstance.interceptors.response.use((res) => res, handle401);
  axiosInstance.interceptors.response.use((res) => res, mapAxiosError);
  return axiosInstance;
};

const handle401 = async (error: AxiosError) => {
  if (error.response?.status === 401) {
    try {
      if (error.config) {
        const session = await fetchAuthSession();
        if (session?.tokens?.accessToken) {
          error.config.headers.Authorization = `Bearer ${session.tokens.accessToken}`;
          return axios.request(error.config);
        }
      }
    } catch (e) {
      if (e instanceof AuthError) {
        window.location.href = '/login';
        return;
      }
    }
  }
  return Promise.reject(error);
};

const mapAxiosError = (error: AxiosError<any>) => {
  switch (error.response?.status) {
    case 404:
      return Promise.reject(new Error('Not Found'));
    case 403:
      return Promise.reject(new ForbiddenError(error.response?.data));
    case 400:
    case 422:
      return Promise.reject(
        new ServerValidationError(
          error.response.data.errors ||
            error.response.data || [{ param: '', msg: 'Something went wrong' }],
        ),
      );
    case 409:
      return Promise.reject(new ConflictError(error.response.data.toString()));
    case 500:
      return Promise.reject(new ServerError(error.response.data.toString()));
    default:
      return Promise.reject(new Error('An error has occurred: ' + error.response?.data));
  }
};
